import { Route } from '@angular/router';
import { AppRoleGuard, RoleGuard } from '@univacity/angular/datas';
import { APP_ROLE, USER_TYPE, RoutePath } from '@univacity/shared/types';
import { emailTemplateRoutes } from './email-template/templates.routes';
import { organisationRoutes } from './organisation/organisation.routes';
import { userRoutes } from './user/user.routes';
import { countryRoutes } from './components-settings-page/country-page/country.routes';
import { visaDashboardRoutes } from './visa/visa-dasboard.routes';

export const settingsRoutes: Route[] = [
  {
    path: '',
    loadComponent: () =>
      import('./settings-layout/settings-layout.component').then(
        (c) => c.SettingsLayoutComponent
      ),
    children: [
      {
        path: '',
        redirectTo: RoutePath.profile,
        pathMatch: 'full',
      },
      {
        path: RoutePath.profile,
        loadComponent: () =>
          import('./profile-setting-page/profile-setting-page.component').then(
            (c) => c.ProfileSettingPageComponent
          ),
      },
      {
        path: RoutePath.referrals,
        loadComponent: () =>
          import(
            './referral-setting-page/referral-setting-page.component'
          ).then((c) => c.ReferralSettingPageComponent),
      },
      {
        path: RoutePath.cards,
        loadComponent: () =>
          import('./cards-page/cards-page.component').then(
            (c) => c.CardsPageComponent
          ),
      },
      {
        path: RoutePath.banks,
        loadComponent: () =>
          import('./banks-page/banks-page.component').then(
            (c) => c.BanksPageComponent
          ),
        canActivate: [AppRoleGuard, RoleGuard],
        canActivateChild: [AppRoleGuard, RoleGuard],
        data: {
          appRole: [APP_ROLE.admin, APP_ROLE.finance],
          roles: [USER_TYPE.admin, USER_TYPE.organisation],
        },
      },
      {
        path: RoutePath.notifications,
        loadComponent: () =>
          import(
            './notification-setting-page/notification-setting-page.component'
          ).then((c) => c.NotificationSettingPageComponent),
      },
      {
        path: RoutePath.components,
        loadComponent: () =>
          import(
            './components-settings-page/components-settings-page.component'
          ).then((c) => c.ComponentsSettingsPageComponent),
        canActivate: [RoleGuard],
        canActivateChild: [RoleGuard],
        data: {
          roles: [USER_TYPE.admin, USER_TYPE.organisation],
        },
      },
      {
        path: RoutePath.preferences,
        loadComponent: () =>
          import(
            './preferences-settings-page/preferences-settings-page.component'
          ).then((c) => c.PreferencesSettingPageComponent),
      },
      {
        path: RoutePath.organisation,
        children: organisationRoutes,
        canActivate: [RoleGuard],
        canActivateChild: [RoleGuard],
        data: {
          roles: [USER_TYPE.admin],
        },
      },
      {
        path: RoutePath.visa,
        children: visaDashboardRoutes,
        canActivate: [RoleGuard],
        canActivateChild: [RoleGuard],
        data: {
          roles: [USER_TYPE.admin],
        },
      },
      {
        path: RoutePath.country,
        children: countryRoutes,
        canActivate: [RoleGuard],
        canActivateChild: [RoleGuard],
        data: {
          roles: [USER_TYPE.admin],
        },
      },
      {
        path: RoutePath.emailTemplates,
        children: emailTemplateRoutes,
        canActivate: [AppRoleGuard, RoleGuard],
        canActivateChild: [AppRoleGuard, RoleGuard],
        data: {
          appRole: [APP_ROLE.admin, APP_ROLE.editor],
          roles: [USER_TYPE.admin],
        },
      },
      {
        path: RoutePath.intakes,
        loadComponent: () =>
          import('./intakes-page/intakes-page.component').then(
            (c) => c.IntakesPageComponent
          ),
        canActivate: [RoleGuard],
        canActivateChild: [RoleGuard],
        data: {
          roles: [USER_TYPE.admin, USER_TYPE.organisation],
        },
      },
      {
        path: RoutePath.branches,
        loadComponent: () =>
          import('./branch/branches-page.component').then(
            (c) => c.BranchesPageComponent
          ),
        canActivate: [RoleGuard],
        canActivateChild: [RoleGuard],
        data: {
          roles: [USER_TYPE.admin, USER_TYPE.organisation],
        },
      },
      {
        path: RoutePath.payables,
        loadComponent: () =>
          import('./payables-page/payables-page.component').then(
            (c) => c.PayablesPageComponent
          ),
        canActivate: [RoleGuard],
        canActivateChild: [RoleGuard],
        data: {
          roles: [USER_TYPE.admin, USER_TYPE.organisation],
          appRole: [APP_ROLE.admin, APP_ROLE.finance],
        },
      },
      {
        path: `${RoutePath.compliance}`,
        loadComponent: () =>
          import(
            './compliance/compliance-list-page/compliance-list-page.component'
          ).then((c) => c.ComplianceListPageComponent),
        canActivate: [RoleGuard],
        canActivateChild: [RoleGuard],
        data: {
          roles: [USER_TYPE.admin],
        },
      },
      {
        path: `${RoutePath.compliance}/:id`,
        loadComponent: () =>
          import(
            './compliance/compliance-detail-page/compliance-detail-page.component'
          ).then((c) => c.ComplianceDetailPageComponent),
        canActivate: [RoleGuard],
        canActivateChild: [RoleGuard],
        data: {
          roles: [USER_TYPE.admin, USER_TYPE.organisation],
          appRole: [APP_ROLE.admin, APP_ROLE.finance],
        },
      },
      {
        path: `${RoutePath.compliance}/:id/${RoutePath.edit}`,
        loadComponent: () =>
          import('./compliance/compliance-form/compliance-form.component').then(
            (c) => c.ComplianceFormComponent
          ),
        canActivate: [RoleGuard],
        canActivateChild: [RoleGuard],
        data: {
          roles: [USER_TYPE.admin, USER_TYPE.organisation],
          appRole: [APP_ROLE.admin],
        },
      },
      {
        path: RoutePath.addons,
        loadComponent: () =>
          import('./addons-page/addons-page.component').then(
            (c) => c.AddonsPageComponent
          ),
        canActivate: [AppRoleGuard, RoleGuard],
        canActivateChild: [AppRoleGuard, RoleGuard],
        data: {
          appRole: [APP_ROLE.admin, APP_ROLE.finance, APP_ROLE.editor],
          roles: [USER_TYPE.admin],
        },
      },
      {
        path: RoutePath.users.replace('/', ''),
        children: userRoutes,
        canActivate: [RoleGuard],
        canActivateChild: [RoleGuard],
        data: {
          roles: [USER_TYPE.admin, USER_TYPE.organisation],
        },
      },
    ],
  },
];
